import styled, { keyframes } from "styled-components";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../../utils/constants";
import { SECTION_GAP } from "./constants";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: ${SPACING.lg - SPACING.xxs}px
    clamp(${SPACING.md - SPACING.xxs}px, 2.6vw, ${SPACING.xl + SPACING.sm}px);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
`;

// Skeleton primitive — theme-aware via --gradient-skeleton (adapts to light/dark)
export const SkeletonBox = styled.div<{ width?: string; height?: string }>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "20px"};
  background: var(--gradient-skeleton);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ${ANIMATION.easing.easeInOut} infinite;
  border-radius: 4px;
`;

// Mirrors the manual hero's card; a ledger account resolves to the taller LedgerHeader instead
export const HeroCardSkeleton = styled.div`
  border-radius: 12px;
  background: var(--hero-background);
  border: 1px solid var(--color-card-border);
  box-shadow: var(--shadow-card-rest);
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const HeroGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.xl}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: 1.4fr 1fr;
    align-items: start;
  }
`;

export const HeroLeftSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const HeroRightSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const MetricsGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${SPACING.md}px;
  margin-top: ${SPACING.lg}px;
  padding: ${SPACING.md}px;
  border: 1px solid var(--color-card-border);
  border-radius: 10px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${SPACING.lg}px;
    padding: ${SPACING.lg}px 0 0;
    border: none;
    border-top: 1px solid var(--color-border);
    border-radius: 0;
  }
`;

export const MetricCellSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const ActionsRowSkeleton = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: flex;
    gap: ${SPACING.sm}px;
    justify-content: flex-end;
  }
`;

// Mirrors PerformanceCard chrome (radius/shadow/background/padding)
export const SectionCardSkeleton = styled.div`
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  box-shadow: var(--shadow-card-rest);
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  margin-top: ${SECTION_GAP}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
  }
`;

export const SectionHeaderSkeleton = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  margin-bottom: ${SPACING.lg}px;
`;

export const SectionTitleBlockSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const ChartAreaSkeleton = styled(SkeletonBox)`
  height: 260px;

  @media ${QUERIES.tabletAndUp} {
    height: 320px;
  }
`;

export const SkeletonLine = styled(SkeletonBox)<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: 16px;
  border-radius: 6px;
`;

export const RowsSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const ListRowSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
`;

// Workspace and rail; the grid collapses in source order, so one column renders the same order.
export const CommandGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: ${SECTION_GAP}px;

  @media ${QUERIES.laptopLargeAndUp} {
    grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  }
`;

export const Workspace = styled.div`
  min-width: 0;
`;

// The page's own live region: there is no shared announcer component in src/.
export const Announcer = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const SummaryNotice = styled.div`
  margin-top: ${SPACING.sm + SPACING.xs}px;
`;

export const SyncNote = styled.p`
  margin: ${SECTION_GAP}px 0 0;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
`;
